import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '../../store';
import { api } from '../../utils/api';
import { localeDate, tz } from '../../utils/region';
import { menuPill, pageLine, pageToolbar, rowTint, PAGE_INSET } from '../../tabPalette';
import Pagination from '../Pagination';
import { MovePicker } from '../FileLibrary';   // the File Library's own Move to… pop-up, for the Folder button (report 00a7c05a)
import OutboxView from './index';

// ── The Approvals button beside the Document box (Calem, report e58ddb81) ──────────────────────────────────
// "Separate tracking and filing approvals from quotation, invoice and receipt tracking, and make them pop up where I have
// marked with red text below." It comes and goes by the top menu's own rule (Portal: canSeeTab) and opens this page: two
// bubbles, Tracking, the Taken / Return records of the one approvals list (OutboxView), and Filing, the new documents of Our
// Documents whose name waits on an approver. The name is an editable box: Approve passes it as it then reads. The document is
// filed and usable the whole time (server services/ourDocsNameApproval.ts), and nothing is sent or held up by this list.
const HOME = 'approvals_doc';
type Bubble = 'tracking' | 'filing';

const fmtDT = (unix: number) => (unix ? new Date(unix * 1000).toLocaleString(localeDate(), { timeZone: tz(), day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }) : '—');
const th: React.CSSProperties = { textAlign: 'left', fontSize: 12, fontWeight: 400, color: 'var(--text-primary)', padding: '10px 10px 6px', lineHeight: '16px', whiteSpace: 'nowrap', boxShadow: 'inset 0 -1px 0 var(--border-strong)', position: 'sticky', top: 0, background: 'var(--bg-surface)' };
const td: React.CSSProperties = { fontSize: 13, color: 'var(--text-primary)', padding: '9px 10px', borderBottom: '1px solid rgba(0,0,0,0.2)', verticalAlign: 'top' };

type NameRow = { id: string; document: string; folder: string; parentId: string; name: string; by: string; at: number };

// The Folder button (report 00a7c05a: "Let me change the folder where the document is going to be stored, not just the name"):
// it shows the folder the document goes to and opens the File Library's Move to… pop-up, which offers only `allowed` —
// Our Documents' own folders, never an Inactive one (GET /files/name-approvals/folders); `paths` gives each its whole path. Like
// the name box, a folder picked changes nothing until Approve, which moves the document there, then renames and approves it.
function NameLine({ row, paths, allowed, onDone }: { row: NameRow; paths: Map<string, string>; allowed: ReadonlySet<string>; onDone: () => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(row.name);
  const [folderId, setFolderId] = useState(row.parentId);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const approve = async () => {
    setBusy(true); setErr('');
    try {
      // The folder goes only when another was picked, so a page left open never moves a file back that somebody has moved since.
      const r = await api.filesNameApprove(row.id, name.trim(), folderId !== row.parentId ? folderId : undefined);
      if (r.ok === false) { setErr(r.error || t('fna_failed')); setBusy(false); return; }
      onDone();
    } catch (e: any) { setErr(e?.message || t('fna_failed')); setBusy(false); }
  };
  return (
    <tr>
      <td style={{ ...td, whiteSpace: 'normal', maxWidth: 180 }}>
        <a href={`/m/file/${encodeURIComponent(row.id)}?name=${encodeURIComponent(row.name)}`} target="_blank" rel="noopener noreferrer" title={t('fna_open')}
          style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>{row.document}</a>
      </td>
      <td style={{ ...td, whiteSpace: 'normal', maxWidth: 240 }}>
        <button type="button" onClick={() => setPicking(true)} disabled={busy || !allowed.size} title={t('fna_folder_change')} aria-label={t('fna_col_folder')}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 6, width: '100%', boxSizing: 'border-box', padding: '5px 9px', fontSize: 13, lineHeight: 1.35, textAlign: 'left', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: busy || !allowed.size ? 'default' : 'pointer' }}>
          <i className="ti ti-folder" style={{ color: '#E8A33D', fontSize: 15, flexShrink: 0, marginTop: 1 }} aria-hidden />
          <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{paths.get(folderId) || row.folder}</span>
        </button>
        {picking && <MovePicker scope="personal" nodes={[{ id: row.id, parent_id: folderId, is_folder: false, name: name.trim() || row.name }]} start={folderId} only={allowed}
          onClose={() => setPicking(false)} onMove={target => { setFolderId(target); setPicking(false); }} />}
      </td>
      <td style={{ ...td, minWidth: 320 }}>
        <input value={name} onChange={e => setName(e.target.value)} aria-label={t('fna_col_name')}
          style={{ width: '100%', boxSizing: 'border-box', padding: '5px 9px', fontSize: 13, borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }} />
        {err && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }}>{err}</div>}
      </td>
      <td style={td}>{row.by || '—'}</td>
      <td style={{ ...td, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{fmtDT(row.at)}</td>
      <td style={{ ...td, whiteSpace: 'nowrap', textAlign: 'right' }}>
        <button onClick={approve} disabled={busy || !name.trim()}
          style={{ padding: '5px 12px', borderRadius: 999, border: '1px solid', fontWeight: 700, fontSize: 11.5, cursor: busy ? 'wait' : 'pointer', ...menuPill(HOME, true) }}>
          {t('oe_approve')}
        </button>
      </td>
    </tr>
  );
}

function FilingNames() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<NameRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // The folders the Folder buttons offer, read once: until they arrive (or if they cannot be read) the buttons stay shut.
  const [paths, setPaths] = useState<Map<string, string>>(() => new Map());
  const allowed = useMemo(() => new Set(paths.keys()), [paths]);
  useEffect(() => {
    api.filesNameApprovalFolders().then(r => setPaths(new Map((r.folders || []).map(f => [f.id, f.path] as [string, string])))).catch(() => {});
  }, []);
  const load = useCallback(async (p: number) => {
    setErr('');
    try {
      const r = await api.filesNameApprovals(p);
      setRows(r.rows || []); setTotal(Number(r.total) || 0); setPage(Number(r.page) || p); setLimit(Number(r.limit) || 100);
    } catch (e: any) { setErr(e?.message || t('fna_failed')); } finally { setLoading(false); }
  }, [t]);
  useEffect(() => { void load(1); }, [load]);
  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><i className="ti ti-loader" style={{ fontSize: 24, color: 'var(--text-tertiary)' }} aria-hidden /></div>;
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ flex: 1, overflow: 'auto', padding: `0 ${PAGE_INSET}px` }}>
        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '10px 16px' }}>{err}</div>}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>{t('fna_col_document')}</th>
            <th style={th}>{t('fna_col_folder')}</th>
            <th style={th}>{t('fna_col_name')}</th>
            <th style={th}>{t('fna_col_by')}</th>
            <th style={th}>{t('fna_col_at')}</th>
            <th style={{ ...th, width: 90 }} />
          </tr></thead>
          <tbody style={{ background: rowTint(HOME) }}>
            {/* The row's key carries its name and its folder: reloaded under a new name or in another folder, its box and its Folder
                button start from those. */}
            {rows.map(r => <NameLine key={`${r.id}:${r.name}:${r.parentId}`} row={r} paths={paths} allowed={allowed} onDone={() => { void load(page); }} />)}
            {rows.length === 0 && <tr><td style={{ ...td, textAlign: 'center', color: 'var(--text-tertiary)', padding: 40 }} colSpan={6}>{t('fna_empty')}</td></tr>}
          </tbody>
        </table>
      </div>
      {total > limit && <Pagination page={page} total={total} limit={limit} onPage={p => { void load(p); }} />}
    </div>
  );
}

// `tracking` / `filing`: what waits on this login in each, the numbers on the bubbles (GET /api/nav-badges, Portal's poll).
export default function ApprovalsDocView({ tracking, filing }: { tracking: number; filing: number }) {
  const { t } = useTranslation();
  const approves = useStore(s => s.me?.approves);
  const [searchParams] = useSearchParams();
  const [picked, setPicked] = useState<Bubble | null>(null);
  const offered: Bubble[] = (['tracking', 'filing'] as Bubble[]).filter(b => (b === 'tracking' ? !!approves?.tracking : !!approves?.filing));
  // Until one is picked: the Filing bubble for a name opened from the red number's list (?focus=), else the first with
  // something waiting. A login with only one of the two kinds is offered that one.
  const lead: Bubble = searchParams.get('focus') || (tracking === 0 && filing > 0) ? 'filing' : 'tracking';
  const open: Bubble = offered.includes(picked as Bubble) ? (picked as Bubble) : offered.includes(lead) ? lead : (offered[0] || lead);
  const bubble = (key: Bubble, label: string, n: number) => (
    <button key={key} onClick={() => setPicked(key)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 999, border: '1px solid', fontSize: 12, fontWeight: 600, cursor: 'pointer', ...menuPill(HOME, open === key), ...(open !== key ? { borderColor: pageLine(HOME) } : {}) }}>
      {label}
      {n > 0 && <span style={{ minWidth: 16, height: 16, padding: '0 4px', boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', borderRadius: 999, background: '#DC2626', color: '#fff', fontSize: 10.5, fontWeight: 800 }}>{n > 99 ? '99+' : n}</span>}
    </button>
  );
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, background: 'var(--bg-surface)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', ...pageToolbar(), background: 'var(--c-gray-50)', flexWrap: 'wrap' }}>
        {offered.includes('tracking') && bubble('tracking', t('tab_taken_return'), tracking)}
        {offered.includes('filing') && bubble('filing', t('tab_upload_doc'), filing)}
        {open === 'filing' && <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('fna_hint')}</span>}
      </div>
      {open === 'filing' ? <FilingNames /> : <OutboxView group="tracking" />}
    </div>
  );
}
